import * as Yup from 'yup'
import { TaxWithholdingValueType, IraConstraintsResponsePayload } from '@Types'
import { map, mapValues } from 'lodash'
import { TransferFrequencyType, TransferRecurrenceType, WhenToSendUnion } from '../TransferFrequency/Types'
import moment from 'moment'

const isDateValid = (dateValue: string | Date) => {
  const formattedDate = new Date(dateValue).toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
  return !isNaN(new Date(dateValue).getTime()) && moment(formattedDate, 'MM/DD/YYYY', true).isValid()
}

export const achTransferSchema = (
  defaultValues: { accountNumber: string },
  iraConstraints: IraConstraintsResponsePayload
) => {
  return Yup.object({
    totalBalanceCheckbox: Yup.boolean().required().default(false),
    fromAccount: Yup.string().required('Required').default(''),
    toAccount: Yup.string().required('Required').default(''),
    transferAmountDisplay: Yup.string()
      .when('totalBalanceCheckbox', {
        is: false,
        then: Yup.string().required('Required')
      })
      .default(''),
    transferAmountRaw: Yup.number()
      .when('fromAccount', {
        is: (value: string | undefined) => value?.includes(defaultValues.accountNumber),
        then: Yup.number().nullable().required('Required').typeError('')
      })
      .when('totalBalanceCheckbox', {
        is: false,
        then: Yup.number()
          .nullable()
          .required('Required')
          .typeError('')
          .min(0.01, 'Amount must be greater than $0.00')
          .default(0)
      })
      .when('totalBalanceCheckbox', {
        is: true,
        then: () => {
          return Yup.number()
            .nullable()
            .test({
              // if Contribution then test fails
              message: () => 'Total Account Balance is only applicable to distributions.',
              test: function (value, context) {
                return (
                  context.parent?.fromAccount === defaultValues.accountNumber &&
                  context.parent?.toAccount !== defaultValues.accountNumber
                )
              }
            })
            .test({
              // if IRA and is Distribution and totalDistributionAllowed is false then test fails
              message: () => 'This type of IRA account does not support a total amount transfer.',
              test: function (value, context) {
                if (iraConstraints.retirementAccount) {
                  return (
                    !!iraConstraints.distributionConstraints?.totalDistributionAllowed &&
                    context.parent?.fromAccount === defaultValues.accountNumber &&
                    context.parent?.toAccount !== defaultValues.accountNumber
                  )
                }
                return true
              }
            })
        }
      })
      .default(0),
    ...(!!iraConstraints.distributionConstraints && {
      distributionReason: Yup.string()
        .when('fromAccount', {
          is: (val: string) => val && val.length > 0 && val === defaultValues.accountNumber,
          then: Yup.string().required('Required')
        })
        .default(''),
      federalTaxWithholdingType: Yup.mixed<TaxWithholdingValueType | ''>().default(''),
      federalTaxWithholdingAmountRaw: Yup.number().when('fromAccount', {
        is: (val: string) => val && val.length > 0 && val === defaultValues.accountNumber,
        then: Yup.number().when('federalTaxWithholdingType', {
          is: (type: string) => type && type.length > 0,
          then: Yup.number()
            .when('federalTaxWithholdingType', {
              is: TaxWithholdingValueType.Fixed,
              then: Yup.number()
                .nullable()
                .required('Required')
                .typeError('')
                .min(0.01, 'Amount must be greater than $0.00')
            })
            .when('federalTaxWithholdingType', {
              is: TaxWithholdingValueType.Percent,
              then: Yup.number()
                .nullable()
                .required('Required')
                .typeError('')
                .min(0.01, 'Amount must be greater than 0%')
                .max(100, 'Amount must be lower than 100%')
            })
        })
      }),
      federalTaxWithholdingAmountDisplay: Yup.string()
        .when('federalTaxWithholdingType', {
          is: (type: string) => type && type.length > 0 && type === 'FIXED',
          then: Yup.string().required('Required')
        })
        .default(''),

      stateTaxWithholdingType: Yup.mixed<TaxWithholdingValueType>().oneOf(map(TaxWithholdingValueType)),
      stateTaxWithholdingAmountRaw: Yup.number().when('fromAccount', {
        is: (val: string) => val && val.length > 0 && val === defaultValues.accountNumber,
        then: Yup.number().when('stateTaxWithholdingType', {
          is: (type: string) => type && type.length > 0,
          then: Yup.number()
            .when('stateTaxWithholdingType', {
              is: TaxWithholdingValueType.Fixed,
              then: Yup.number()
                .nullable()
                .required('Required')
                .typeError('')
                .min(0.01, 'Amount must be greater than $0.00')
            })
            .when('stateTaxWithholdingType', {
              is: TaxWithholdingValueType.Percent,
              then: Yup.number()
                .nullable()
                .required('Required')
                .typeError('')
                .min(0.01, 'Amount must be greater than 0%')
                .max(100, 'Amount must be lower than 100%')
            })
        })
      }),
      stateTaxWithholdingAmountDisplay: Yup.string().when('fromAccount', {
        is: (val: string) => val && val.length > 0 && val === defaultValues.accountNumber,
        then: Yup.string().when('stateTaxWithholdingType', {
          is: (type: string) => type && type.length > 0 && type === 'FIXED',
          then: Yup.string().required('Required')
        })
      })
    }),
    ...(!!iraConstraints.contributionConstraints && {
      contributionType: Yup.string()
        .when('toAccount', {
          is: (val: string) => val && val.length > 0 && val === defaultValues.accountNumber,
          then: Yup.string().required('Required')
        })
        .default(''),

      ...(!iraConstraints.contributionConstraints.onlyAllowCurrentYearContribution && {
        contributionYear: Yup.string()
          .when(['contributionType', 'toAccount'], {
            is: (contributionType: string, toAccount: string) => {
              const isDeposit = toAccount === defaultValues.accountNumber
              const doesSelectedTypeAllowPrevious = iraConstraints.contributionConstraints?.validContributions.find(
                (item) => item.reason === contributionType
              )?.previousYearContributionAllowed
              return (
                isDeposit &&
                !iraConstraints.contributionConstraints?.onlyAllowCurrentYearContribution &&
                !!doesSelectedTypeAllowPrevious
              )
            },
            then: Yup.string().required()
          })
          .default('')
      })
    }),
    transferRecurrence: Yup.mixed<TransferRecurrenceType>().default('ONE_TIME'),
    whenToSend: Yup.mixed<WhenToSendUnion>().default('IMMEDIATELY'),
    transferScheduledDate: Yup.string()
      .when('whenToSend', {
        is: 'IN_THE_FUTURE',
        then: Yup.string()
          .test({
            message: () => '',
            test: function (value, context) {
              if (!!value) {
                return isDateValid(value)
              }
              return true
            }
          })
          .required('Required')
          .default('')
      })
      .default(''),
    transferFrequency: Yup.mixed<TransferFrequencyType>()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.mixed<TransferFrequencyType>().required('Required').default('WEEKLY')
      })
      .default('WEEKLY'),
    transferStartDate: Yup.string()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.string()
          .test({
            message: () => 'Invalid date',
            test: function (value) {
              if (!!value) {
                return isDateValid(value)
              }
              return true
            }
          })
          .test({
            message: () => 'Recurring transfers not allowed for total balance transfers',
            test: function (value, context) {
              return !context.parent.totalBalanceCheckbox
            }
          })
          .required('Required')
          .default('')
      })
      .default(''),
    numberOfTransfers: Yup.number()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.number().max(100, 'Maximum 100').min(1, 'Value must be greater than 0').default(undefined)
      })
      .default(undefined),
    fees: Yup.lazy((obj) =>
      Yup.object(
        mapValues(obj, () =>
          Yup.object({
            chargeCustomer: Yup.boolean().default(false),
            feeTotal: Yup.number().default(0)
          })
        )
      ).default({})
    )
  })
}
